/**
 * Storage keys aligned with legacy `autoLaunchController.js` (`SKNY_AUTO_*`).
 */

export function buildAutoLaunchKeyBase(tenantId: string, siteId: string, scope = ""): string {
  const t = tenantId.trim() || "t";
  const s = siteId.trim() || "s";
  return `SKNY_AUTO_${t}_${s}${scope ? `_${scope}` : ""}`;
}

export function autoLaunchStorageKeys(tenantId: string, siteId: string, scope = "") {
  const base = buildAutoLaunchKeyBase(tenantId, siteId, scope);
  return {
    KEY_LAST_TS: `${base}_LAST`,
    KEY_DONE: `${base}_DONE`,
    KEY_IMPRESS: `${base}_IMP`,
    KEY_USED: `${base}_USED`,
    KEY_CFG: `${base}_CFG`,
    KEY_CLOSE_TS: `${base}_CLOSE`,
    /**
     * #155 Item 4 — durable "this visitor has engaged here before" flag.
     * Written by `EngageChatInterface` on the first successful user
     * message of any session. Lives in `localStorage` (cross-session,
     * cross-tab) and is wiped by `clearAllAutoLaunchStorage` so the
     * "Forget me" flow correctly resets the suppression signal.
     */
    KEY_ENGAGED_TS: `${base}_ENGAGED`,
  } as const;
}

function ssGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function ssRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function lsRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Wipe every `SKNY_AUTO_*` key (impression count, session-done flag,
 * cooldown last-fire, manual-close cooldown, random message rotation,
 * cached config id, returning-visitor engaged hint) for a given
 * tenant/site. Used by the Forget Me flow so the next page load
 * behaves like a brand-new visitor — including resetting the
 * returning-visitor suppression signal so a future auto-launch can
 * fire normally.
 */
export function clearAllAutoLaunchStorage(
  tenantId: string,
  siteId: string,
  scope = '',
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const {
    KEY_LAST_TS,
    KEY_DONE,
    KEY_IMPRESS,
    KEY_USED,
    KEY_CFG,
    KEY_CLOSE_TS,
    KEY_ENGAGED_TS,
  } = autoLaunchStorageKeys(tenantId, siteId, scope);
  ssRemove(KEY_DONE);
  ssRemove(KEY_IMPRESS);
  ssRemove(KEY_USED);
  ssRemove(KEY_CFG);
  lsRemove(KEY_LAST_TS);
  lsRemove(KEY_CLOSE_TS);
  lsRemove(KEY_ENGAGED_TS);
}

/** Idempotent: records manual panel close for cooldown gating (legacy `onPanelClose({ manual: true })`). */
export function recordWidgetManualPanelClose(tenantId: string, siteId: string, scope = ""): void {
  if (typeof window === "undefined") {
    return;
  }
  const { KEY_CLOSE_TS } = autoLaunchStorageKeys(tenantId, siteId, scope);
  lsSet(KEY_CLOSE_TS, String(Date.now()));
}

export function readSessionDone(tenantId: string, siteId: string, scope = ""): boolean {
  return ssGet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_DONE) === "1";
}

export function setSessionDone(tenantId: string, siteId: string, scope = ""): void {
  ssSet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_DONE, "1");
}

export function readImpressionCount(tenantId: string, siteId: string, scope = ""): number {
  const raw = ssGet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_IMPRESS);
  const n = parseInt(raw || "0", 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function incrementImpressionCount(tenantId: string, siteId: string, scope = ""): void {
  const { KEY_IMPRESS } = autoLaunchStorageKeys(tenantId, siteId, scope);
  const next = readImpressionCount(tenantId, siteId, scope) + 1;
  ssSet(KEY_IMPRESS, String(next));
}

export function readCooldownLastTs(tenantId: string, siteId: string, scope = ""): number {
  const raw = lsGet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_LAST_TS);
  const n = parseInt(raw || "0", 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function writeCooldownLastTs(tenantId: string, siteId: string, scope = ""): void {
  const { KEY_LAST_TS } = autoLaunchStorageKeys(tenantId, siteId, scope);
  lsSet(KEY_LAST_TS, String(Date.now()));
}

export function readManualCloseTs(tenantId: string, siteId: string, scope = ""): number {
  const raw = lsGet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_CLOSE_TS);
  const n = parseInt(raw || "0", 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function readStoredConfigId(tenantId: string, siteId: string, scope = ""): string {
  return ssGet(autoLaunchStorageKeys(tenantId, siteId, scope).KEY_CFG) || "";
}

export function writeStoredConfigId(tenantId: string, siteId: string, configId: string, scope = ""): void {
  if (!configId) {
    return;
  }
  const { KEY_CFG, KEY_USED } = autoLaunchStorageKeys(tenantId, siteId, scope);
  const prev = ssGet(KEY_CFG) || "";
  if (prev && prev !== configId) {
    ssSet(KEY_USED, "[]");
  }
  ssSet(KEY_CFG, configId);
}

export function readRandomUsedKeys(tenantId: string, siteId: string, scope = ""): string[] {
  const { KEY_USED } = autoLaunchStorageKeys(tenantId, siteId, scope);
  const raw = ssGet(KEY_USED);
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return [];
  }
}

export function writeRandomUsedKeys(tenantId: string, siteId: string, keys: string[], scope = ""): void {
  const { KEY_USED } = autoLaunchStorageKeys(tenantId, siteId, scope);
  try {
    ssSet(KEY_USED, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// #155 Item 4 — Returning-visitor engaged hint
// ---------------------------------------------------------------------------

/**
 * Returns `true` iff the visitor has engaged (sent at least one
 * message) on this tenant/site at any point in the past, as recorded
 * by `writeVisitorEngagedHint`. SSR-safe (returns `false` when
 * `window` is undefined) and storage-failure-safe (returns `false`
 * when `localStorage` is unavailable, e.g. private mode).
 */
export function readVisitorEngagedHint(
  tenantId: string,
  siteId: string,
  scope = "",
): boolean {
  if (typeof window === "undefined") return false;
  const { KEY_ENGAGED_TS } = autoLaunchStorageKeys(tenantId, siteId, scope);
  const raw = lsGet(KEY_ENGAGED_TS);
  if (raw == null) return false;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0;
}

/**
 * Record that the visitor has engaged on this tenant/site. Idempotent:
 * called from `EngageChatInterface` on every successful user message;
 * the hint is a boolean signal — value content (timestamp) is for
 * debuggability only. Storage failures are silently swallowed so a
 * blocked-storage browser never breaks the chat path.
 */
export function writeVisitorEngagedHint(
  tenantId: string,
  siteId: string,
  scope = "",
): void {
  if (typeof window === "undefined") return;
  const { KEY_ENGAGED_TS } = autoLaunchStorageKeys(tenantId, siteId, scope);
  lsSet(KEY_ENGAGED_TS, String(Date.now()));
}
